import React from 'react';
import PropTypes from 'prop-types';
import Badge from '../../../components/ui/Badge';
import useEmployeeReportText from '../employeeReportText';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';

export const initialsOf = (row) => `${(row.name || '').charAt(0)}${(row.surname || '').charAt(0)}`.toLocaleUpperCase('tr-TR') || '?';
export const GRADIENT = 'tw-from-sky-400 tw-to-indigo-600 tw-shadow-indigo-500/30';

/** Tooltip / plain text of a cell. */
export const useAssignmentText = () => {
  const { formatDate } = useEmployeeReportText();
  return (row, key) => {
    switch (key) {
      case 'name': return row.fullName;
      case 'center': return row.centerName;
      case 'project': return row.projectTitle;
      case 'role': return row.role;
      case 'rate': return row.rateText;
      case 'assignedAt': return formatDate(row.assignedAt);
      default: return '';
    }
  };
};

/** Content of one assignment table cell. */
const AssignmentCell = ({ columnKey, row }) => {
  const text = useAssignmentText()(row, columnKey);
  if (columnKey === 'name') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
        <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${GRADIENT}`}>
          {initialsOf(row)}
        </span>
        <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{text || '-'}</span>
      </span>
    );
  }
  if (!text) return <span className={MUTED}>-</span>;
  if (columnKey === 'project') return <Badge tone="info">{text}</Badge>;
  return <span className="tw-block tw-truncate">{text}</span>;
};

AssignmentCell.propTypes = { columnKey: PropTypes.string.isRequired, row: PropTypes.object.isRequired };

/** One row of the card list (narrow screens). */
export const AssignmentCardRow = ({ row }) => (
  <>
    <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${GRADIENT}`}>
      {initialsOf(row)}
    </span>
    <span className="tw-min-w-0 tw-flex-1">
      <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{row.fullName || '-'}</span>
        {row.projectTitle && <Badge tone="info">{row.projectTitle}</Badge>}
      </span>
      <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">
        {[row.role, row.centerName].filter(Boolean).join(' · ') || '-'}
      </span>
    </span>
  </>
);

AssignmentCardRow.propTypes = { row: PropTypes.object.isRequired };

export default AssignmentCell;
